import { useState } from 'react';
import { useMutation } from 'react-query';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { groupsApi } from '../../services/groups-api-client';
import { groupToPayload } from '../../lib/groupPayload';
import { mutationErrorMessage } from '../../lib/mutation-error';
import { isOwner } from '../../lib/groupHosts';
import type { GroupDetail } from '../../types/groups';

/**
 * Publish/unpublish, promoted off the create form.
 *
 * Before this, `published` could only be set once, at creation — flip it wrong
 * there, or need to take a live trip down for a fix, and nothing in Studio
 * could change it again. A prod QA group on 2026-09-24 had to be published with
 * a hand-built PATCH because no UI control existed. The Draft/Published chip on
 * the group page was read-only for the same reason: it showed the state, but
 * nothing next to it could change it.
 *
 * Owner-only to match the co-host pattern in HostsCard: the server is the real
 * gate (a non-owner PATCH is refused there), so this only stops offering a
 * control that would always fail. Anyone else still sees the plain chip.
 */
export default function PublishToggle({ group, onSaved }: { group: GroupDetail; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const owner = isOwner(group.viewerRole);
  const next = !group.published;
  const inviteOnly = (group.accessMode || 'invite_only') === 'invite_only';

  const mutation = useMutation(
    () => groupsApi.updateGroup(group.id, groupToPayload(group, { published: next })),
    {
      onSuccess: () => {
        setOpen(false);
        onSaved();
      },
    },
  );

  const chip = (
    <Chip
      label={group.published ? 'Published' : 'Draft'}
      color={group.published ? 'success' : 'default'}
      onClick={owner ? () => setOpen(true) : undefined}
      // MUI only adds the hand cursor and hover state when onClick is present,
      // so a non-owner's chip stays visibly inert rather than looking clickable
      // and then doing nothing.
    />
  );

  if (!owner) return chip;

  return (
    <>
      {chip}
      <Dialog open={open} onClose={() => { setOpen(false); mutation.reset(); }}>
        <DialogTitle>{next ? 'Publish this group?' : 'Unpublish this group?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {next
              ? inviteOnly
                ? 'Publishing makes the guest page reachable — but this group is invite-only, so guests '
                  + 'can only open it through their own invite link. The shared /g/ link still refuses a '
                  + 'visitor with no token.'
                : 'Publishing makes the guest page reachable at the shared link. Anyone with the address '
                  + 'will be able to open it.'
              : 'Unpublishing takes the guest page down: guests cannot open it, RSVP, request a room or '
                + 'claim a dinner seat until you publish again. Nothing already saved is deleted. Invites '
                + 'and broadcasts cannot be sent while it is unpublished, and a scheduled send that comes '
                + 'due fails instead of going out.'}
          </DialogContentText>
          {mutation.error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {mutationErrorMessage(mutation.error) || `Could not ${next ? 'publish' : 'unpublish'} this group.`}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpen(false); mutation.reset(); }} disabled={mutation.isLoading}>Cancel</Button>
          <Button variant="contained" onClick={() => mutation.mutate()} disabled={mutation.isLoading}>
            {mutation.isLoading ? 'Saving…' : next ? 'Publish' : 'Unpublish'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
